import { useBrainStore } from '@/state/brainStore';
import { resolveNoRebookFlag } from '@/domain/resolveNoRebookFlow';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { playSound } from '@/sound/audioSystem';
import { latestWaiverForBooking } from '@/domain/waiverFlow';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { flagNoRebookMidPiece } from '@/domain/noRebookFlow';
import { cashLedgerFor } from '@/domain/payoutFlow';
import { depositPaidAud } from '@/utils/bookingSummary';
import { seedNow, seedId } from '@/domain/seedContext';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { checkoutRebookIntent } from '@/api/checkoutRebookIntent';
import {
  floorCheckoutIntent,
  sessionPhotosIntent,
  type ServerCheckoutEcho,
  type ServerLedgerFigures,
} from '@/api/floorCheckoutIntent';
import { flushWritesAndWait } from '@/state/writeQueue';
import { applyDepositIntent, type IntentErrorKind } from '@/api/intents';
import {
  availableToApplyAud,
  isLiveBooking,
  onFileExpiryFrom,
  planDepositRemainder,
  remainderCaseFor,
  type DepositRemainderPlan,
  type RemainderCase,
} from '@atink/domain';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { useUIStore } from '@/state/uiStore';
import { echoTerminalDocket } from '@/domain/acquirerSim';
import { absorbCachedViews } from '@/api/liveStream';
import type {
  ArtistRebookIntent,
  Booking,
  Deposit,
  FutureReadinessSignal,
  MerchSaleLine,
  Piece,
  RebookAttribution,
  RebookOutcome,
  SalePayment,
  SessionPhotoKind,
  Waiver,
} from '@/domain/types';

function nowIso() {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

/**
 * Payments recorded at/after this sim hour belong to tomorrow's settlement lump
 * (the banking-lump cutoff). DISTINCT from CLOCKOFF_HOUR (18, paymentTiming.ts),
 * which is the 6pm collection clock-off - a different concept. Do not merge.
 */
export const SETTLEMENT_CUTOFF_HOUR = 17;

/** Hour-of-day in the ISO string's OWN offset (seeds share +10:00), cutoff-safe. */
function hourOfIso(iso: string): number {
  return Number(iso.slice(11, 13));
}

/**
 * Auto-stamped make-sale reference (Prompt E, client amendment). Replaces the
 * hand-written EFTPOS docket: it rides the transaction and is echoed back at
 * settlement so the bank lump auto-splits (E4). Deterministic from the booking +
 * day so re-runs are stable (no Date.now / Math.random).
 */
export function makeSaleReference(studioId: string, bookingId: string, atIso: string): string {
  const stu = studioId.replace(/[^a-z]/gi, '').slice(0, 3).toUpperCase().padEnd(3, 'X');
  const mmdd = atIso.slice(5, 7) + atIso.slice(8, 10);
  let hash = 0;
  for (let i = 0; i < bookingId.length; i += 1) hash = (hash * 31 + bookingId.charCodeAt(i)) >>> 0;
  const seq = (hash % 10000).toString().padStart(4, '0');
  return `MS-${stu}-${mmdd}-${seq}`;
}

/**
 * Stable chair number for an artist — one artist per chair. Deterministic from
 * the artist id so a seated booking always shows that artist's usual chair,
 * rather than a random booth. Used only as a fallback when the caller doesn't
 * pass an explicit booth (the floor UI seats by artist, not by picking a booth).
 */
function chairForArtist(artistId: string): number {
  let hash = 0;
  for (let i = 0; i < artistId.length; i += 1) hash = (hash * 31 + artistId.charCodeAt(i)) >>> 0;
  return (hash % 6) + 1;
}

/**
 * Result of a check-in attempt. Check-in is a hard-gated step: the client can
 * only be checked in once their medical waiver is signed. The failure is typed
 * (not a hidden button) so no UI path can bypass the gate — every surface that
 * offers "Check in" must handle the `waiver-unsigned` case.
 */
export type CheckInResult =
  | { ok: true; booking: Booking }
  | { ok: false; reason: 'not-found' }
  | { ok: false; reason: 'waiver-unsigned'; waiver?: Waiver };

/**
 * Step 1 of the floor flow. Marks the client as arrived at the desk
 * (`status: 'checked-in'` + `checkedInAt`). It deliberately does NOT seat them
 * or assign a booth — that is `seatBooking` (step 2).
 *
 * Waiver gate: the latest waiver for the booking must be `signed`, else the
 * check-in is refused with `reason: 'waiver-unsigned'`.
 *
 * Idempotent: calling it again once the client is already checked in / seated /
 * completed is a no-op that reports success.
 */
export function checkInBooking(bookingId: string): CheckInResult {
  const brain = useBrainStore.getState();
  // Gate on what the Floor shows (tv-2na.11.5): a waiver signed on the
  // customer's device reaches this tab only through the live views, so fold them
  // into the store (forward-only) before reading it.
  // calendar-11 (b): a booking made on another device may only be in the views yet.
  if (!brain.bookings[bookingId] || latestWaiverForBooking(bookingId)?.status !== 'signed') absorbCachedViews();
  const booking = useBrainStore.getState().bookings[bookingId];
  if (!booking) return { ok: false, reason: 'not-found' };
  // Already at or past check-in — nothing to do, the gate is behind them.
  if (
    booking.status === 'checked-in' ||
    booking.status === 'in-chair' ||
    booking.status === 'completed'
  ) {
    return { ok: true, booking };
  }

  const waiver = latestWaiverForBooking(bookingId);
  if (waiver?.status !== 'signed') {
    return { ok: false, reason: 'waiver-unsigned', waiver };
  }

  const at = nowIso();
  const updated: Booking = { ...booking, status: 'checked-in', checkedInAt: at };
  brain.upsertBooking(updated);
  brain.appendActivity({
    id: `act-checkin-${booking.id}`,
    type: 'booking-checked-in',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `${booking.customerId} checked in for their ${booking.sessionLength.replace('-', ' ')} with ${booking.artistId}.`,
    timestamp: at,
  });
  playSound('inbound-ring');
  eventBus.publish('booking:checked-in', { bookingId });
  return { ok: true, booking: updated };
}

/** Result of a seat attempt. Seating is only valid from the `checked-in` state. */
export type SeatResult =
  | { ok: true; booking: Booking }
  | { ok: false; reason: 'not-found' }
  | { ok: false; reason: 'not-checked-in' };

/**
 * Step 2 of the floor flow. Seats a checked-in client into the chair
 * (`status: 'in-chair'` + `seatedAt`) and records the booth. Only valid from
 * `checked-in` — a booking that is still confirmed (never checked in) or already
 * in the chair is refused with `reason: 'not-checked-in'`.
 */
export function seatBooking(bookingId: string, boothNumber?: number): SeatResult {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking) return { ok: false, reason: 'not-found' };
  if (booking.status !== 'checked-in') return { ok: false, reason: 'not-checked-in' };

  const at = nowIso();
  const updated: Booking = {
    ...booking,
    status: 'in-chair',
    seatedAt: at,
    boothNumber: boothNumber ?? booking.boothNumber ?? chairForArtist(booking.artistId),
  };
  brain.upsertBooking(updated);
  brain.appendActivity({
    id: `act-seat-${booking.id}`,
    type: 'booking-seated',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `${booking.customerId} seated in booth ${updated.boothNumber} with ${booking.artistId}.`,
    timestamp: at,
  });
  playSound('inbound-ring');
  eventBus.publish('booking:seated', { bookingId, boothNumber: updated.boothNumber });
  return { ok: true, booking: updated };
}

export interface CheckOutInput {
  bookingId: string;
  sessionNote?: string;
  futureReadinessPhrase?: string;
  futureReadinessTriggerDate?: string;
  hoursToAdd?: number;
  markPieceComplete?: boolean;
  /**
   * L3 (17 Jul): approximate sessions still left on the piece, captured on a
   * decline-path checkout (partway finished). Makes `estimatedTotalSessions` a
   * living number = sessions completed so far + this remainder. Ignored when the
   * piece is marked complete.
   */
  pieceSessionsLeft?: number;
  paymentMethod: 'card' | 'cash' | 'rolled-deposit' | 'mixed';
  amountPaidAud: number;
  rolloverDepositToNextBooking: boolean;
  /** Deposit applied to today's balance (partial deposit use). Defaults to 0. */
  depositAppliedAud?: number;
  /** Merch/aftercare items sold on this checkout (no commission). */
  merchLines?: MerchSaleLine[];
  /** Cash physically tendered (cash/mixed) — net (tendered − change) hits the ledger. */
  cashTenderedAud?: number;
  /** Reasoned +/- charge adjustment (D3), signed. Defaults to 0. */
  adjustmentAud?: number;
  adjustmentReason?: string;
  /**
   * Captured from the Rebook Outcome step. When present, drives the rebook
   * attribution + (for Outcome A) creates the locked-in next booking, (for
   * Outcome C) seeds the sales follow-up task.
   */
  rebookCapture?: {
    outcome: RebookOutcome;
    attribution: RebookAttribution;
    selectedSlotIso?: string;
    salesNote?: string;
    /** B7 (8xat D08-09): the client is happy to come in earlier — onto the new booking. */
    preferBroughtForward?: boolean;
  };
  /**
   * Floor came-down gate (Diagram 10): whether the artist walked the client down
   * to checkout. The UI enforces a non-blank choice; 'did-not-come-down' raises
   * a tracked accountability issue on the activity feed.
   */
  cameDown?: CameDown;
  /** Agent id of whoever ran this checkout (floor attribution, tv-ona). */
  checkedOutBy?: string;
}

export type CameDown = 'came-down' | 'did-not-come-down';

export interface CheckOutResult {
  bookingId: string;
  pieceId?: string;
  pieceCompleted?: boolean;
  rolledDepositId?: string;
  /** Populated when Outcome A creates the next session inline. */
  newBookingId?: string;
}

/**
 * An in-chair rebook the checkout-rebook intent has ALREADY committed
 * (tv-2na.8.5): the next session and, when the deposit rolls, the rolled deposit
 * + the rolled-over original. `checkOutBooking` mirrors these into the store
 * without bridge PUTs instead of minting its own.
 */
export interface ServerRebook {
  nextBooking: Booking;
  roll?: { next: Deposit; originalRolled: Deposit };
}

/**
 * The deposit roll at checkout (G2): a fresh paid deposit carrying ONE
 * `rollover-in`, and the original -> rolled-over with an APPENDED `rollover-out`
 * (a single `paid` backfilled first when it has no ledger, so it nets to zero).
 * `toBookingId`: the next session the money rolls onto, when there is one.
 */
function buildRollover(
  booking: Booking,
  original: Deposit,
  nextId: string,
  at: string,
  toBookingId?: string,
): { next: Deposit; originalRolled: Deposit } {
  // The money that actually rolls = what is still free on the original (ontheday-7
  // b, d: earlier redemptions are used), and the rolled deposit is FOR that amount.
  const paidSoFar = availableToApplyAud(original);
  const next: Deposit = {
    id: nextId,
    customerId: original.customerId,
    studioId: original.studioId,
    artistId: original.artistId ?? booking.artistId,
    amountAud: paidSoFar,
    feesAud: 0,
    netAud: paidSoFar,
    status: 'paid',
    channelSent: original.channelSent,
    stripePaymentLinkId: `plink_roll_${booking.id.slice(0, 8)}`,
    // No next session (a "will rebook later" pot): the money is ON FILE, valid 12
    // months like every dateless deposit (ruling 24 Sep, board 02). A roll onto a
    // real next session keeps the 30-day link window.
    expiresAt: toBookingId
      ? new Date(new Date(at).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString()
      : onFileExpiryFrom(at),
    createdAt: at,
    viewedAt: at,
    paidAt: at,
    policy: original.policy,
    source: 'in-chair-rebooking',
    // A real next session: the rolled money sits on it (the audit's null
    // booking_id is what left recon showing it outstanding).
    ...(toBookingId ? { bookingId: toBookingId, amountPaid: paidSoFar } : {}),
    // Movement (G2): the money arrived here, linked back to the original.
    ledger: [
      {
        at,
        amountAud: paidSoFar,
        kind: 'rollover-in',
        counterpartDepositId: original.id,
        fromBookingId: booking.id,
        ...(toBookingId ? { toBookingId } : {}),
        by: 'system',
        note: 'Rolled from previous session',
      },
    ],
  };
  const originalLedger =
    original.ledger && original.ledger.length > 0
      ? original.ledger
      : [
          {
            at: original.paidAt ?? at,
            amountAud: paidSoFar,
            kind: 'paid' as const,
            by: original.takenByAgentId ?? 'system',
          },
        ];
  const originalRolled: Deposit = {
    ...original,
    status: 'rolled-over',
    ledger: [
      ...originalLedger,
      {
        at,
        amountAud: paidSoFar,
        kind: 'rollover-out',
        counterpartDepositId: nextId,
        ...(toBookingId ? { fromBookingId: booking.id, toBookingId } : {}),
        by: 'system',
        note: 'Rolled to next session',
      },
    ],
  };
  return { next, originalRolled };
}

/** The next session locked in the chair: same customer / artist / piece, born confirmed. */
function buildNextSession(
  booking: Booking,
  newBookingId: string,
  slotIso: string,
  rolledDepositId: string | undefined,
  at: string,
  preferBroughtForward?: boolean,
): Booking {
  return {
    ...(preferBroughtForward ? { preferBroughtForward: true } : {}),
    id: newBookingId,
    customerId: booking.customerId,
    // The artist's studio, as the checkout-rebook intent files it (B7).
    studioId: useBrainStore.getState().artists[booking.artistId]?.studioId ?? booking.studioId,
    artistId: booking.artistId,
    pieceId: booking.pieceId,
    start: slotIso,
    durationMin: booking.durationMin,
    sessionLength: booking.sessionLength,
    status: 'confirmed',
    depositId: rolledDepositId,
    designBrief: booking.designBrief,
    referencePhotos: booking.referencePhotos,
    // Q8 (DECIDED 25 Sep, tv-2na.8.22 / D13): a rebook carries the BOOKED price
    // and the structured brief — dropping dayRateAud let it fall back to the
    // rate card ($800 against the $880 the session was booked at).
    ...(booking.dayRateAud != null ? { dayRateAud: booking.dayRateAud } : {}),
    ...(booking.dayRateBasis ? { dayRateBasis: booking.dayRateBasis } : {}),
    ...(booking.placement ? { placement: booking.placement } : {}),
    ...(booking.bodyPart ? { bodyPart: booking.bodyPart } : {}),
    ...(booking.bodyPartSide ? { bodyPartSide: booking.bodyPartSide } : {}),
    ...(booking.bodyPartQualifier ? { bodyPartQualifier: booking.bodyPartQualifier } : {}),
    ...(booking.colour ? { colour: booking.colour } : {}),
    ...(booking.styleTags?.length ? { styleTags: booking.styleTags } : {}),
    ...(booking.summary ? { summary: booking.summary } : {}),
    ...(booking.refsLocation ? { refsLocation: booking.refsLocation } : {}),
    ...(booking.collabArtistIds?.length ? { collabArtistIds: booking.collabArtistIds } : {}),
    source: 'in-chair-rebooking',
    createdAt: at,
    sourceBookingId: booking.id,
    inChairRebooking: true,
  };
}

/** A part-applied deposit's remainder at this checkout (tv-2na.8.17). */
interface CheckoutRemainder {
  remainderCase: RemainderCase;
  remainderDepositId: string;
  appliedAud: number;
  /** rolls-to-rebook: the next session the rest lands on. */
  toBookingId?: string;
}

/**
 * The remainder branch of this checkout, or undefined when there is nothing to
 * carry: no paid deposit, a clean roll (nothing applied — the rollover path), the
 * whole deposit applied, or nothing applied at all. DECIDED 24 Sep (Q1): the rest
 * FOLLOWS THE BOOKING — rebooked: rolls to the rebook; final session / one-off:
 * used up; mid-piece, no rebook: on file 12 months (the Sweeper).
 */
function checkoutRemainder(input: CheckOutInput, booking: Booking, deposit: Deposit | undefined): CheckoutRemainder | undefined {
  if (!deposit || deposit.status !== 'paid' || input.rolloverDepositToNextBooking) return undefined;
  const appliedAud = input.depositAppliedAud ?? 0;
  if (appliedAud <= 0 || appliedAud >= availableToApplyAud(deposit)) return undefined;
  const cap = input.rebookCapture;
  const rebookTo =
    cap?.outcome.kind === 'rebookedInChair' && cap.selectedSlotIso ? cap.outcome.newBookingId : undefined;
  const pieceComplete = input.markPieceComplete === true || cap?.outcome.kind === 'pieceComplete';
  const remainderCase = remainderCaseFor({
    outcomeKind: rebookTo || cap?.outcome.kind !== 'rebookedInChair' ? cap?.outcome.kind : undefined,
    pieceComplete,
    hasPiece: Boolean(booking.pieceId),
  });
  return {
    remainderCase,
    // Deterministic per session, so a retry after a lost response replays.
    remainderDepositId: `dep-rem-${booking.id}`,
    appliedAud,
    ...(remainderCase === 'rolls-to-rebook' && rebookTo ? { toBookingId: rebookTo } : {}),
  };
}

function planRemainder(r: CheckoutRemainder, booking: Booking, deposit: Deposit, at: string): DepositRemainderPlan {
  return planDepositRemainder({
    originalDepositId: deposit.id,
    paidAud: availableToApplyAud(deposit),
    appliedAud: r.appliedAud,
    remainderCase: r.remainderCase,
    atIso: at,
    fromBookingId: booking.id,
    remainderDepositId: r.remainderDepositId,
    ...(r.toBookingId ? { toBookingId: r.toBookingId } : {}),
    ...(booking.pieceId ? { toPieceId: booking.pieceId } : {}),
  });
}

/** The store records the remainder plan produces (the original + the carried remainder). */
function remainderRecords(
  plan: DepositRemainderPlan,
  booking: Booking,
  original: Deposit,
  at: string,
): { original: Deposit; remainder?: Deposit } {
  const priorLedger =
    original.ledger && original.ledger.length > 0
      ? original.ledger
      : [{ at: original.paidAt ?? at, amountAud: depositPaidAud(original), kind: 'paid' as const, by: original.takenByAgentId ?? 'system' }];
  const nextOriginal: Deposit = {
    ...original,
    status: plan.originalStatus,
    ledger: [...priorLedger, ...plan.originalEntries],
  };
  const r = plan.remainder;
  if (!r) return { original: nextOriginal };
  const remainder: Deposit = {
    id: r.id,
    customerId: original.customerId,
    studioId: original.studioId,
    artistId: original.artistId ?? booking.artistId,
    ...(booking.pieceId ? { pieceId: booking.pieceId } : {}),
    ...(r.bookingId ? { bookingId: r.bookingId } : {}),
    amountAud: r.amountAud,
    amountPaid: r.amountAud,
    feesAud: 0,
    netAud: r.amountAud,
    status: 'paid',
    channelSent: original.channelSent,
    stripePaymentLinkId: `plink_rem_${booking.id.slice(0, 12)}`,
    expiresAt: r.expiresAt,
    createdAt: at,
    viewedAt: at,
    paidAt: at,
    policy: original.policy,
    source: 'in-chair-rebooking',
    ledger: [r.entry],
  };
  return { original: nextOriginal, remainder };
}

/**
 * True when this checkout must go through a server intent first on the
 * server-backed build (`completeCheckout`): it locks a next session in the chair
 * (checkout-rebook), or it part-applies the deposit and the rest must follow the
 * booking (apply-deposit, tv-2na.8.17). Every other checkout can run the
 * synchronous `checkOutBooking` directly.
 */
export function needsCheckoutRebookIntent(input: CheckOutInput): boolean {
  if (isStoreFallbackEnabled()) return false;
  const brain = useBrainStore.getState();
  const booking = brain.bookings[input.bookingId];
  if (!booking) return false;
  const cap = input.rebookCapture;
  if (cap?.outcome.kind === 'rebookedInChair' && !!cap.selectedSlotIso) return true;
  return Boolean(checkoutRemainder(input, booking, booking.depositId ? brain.deposits[booking.depositId] : undefined));
}

/** Outcome of `completeCheckout`: the checkout applied, or nothing applied at all. */
export type CheckOutOutcome =
  | { ok: true; result: CheckOutResult }
  | { ok: false; kind: IntentErrorKind; message?: string };

/**
 * The make-sale checkout, server-backed (tv-2na.8.5, Diagram 07: "a rebook rolls
 * the deposit to a real next session"). When the checkout locks a next session
 * in the chair, the next session + deposit roll + piece link go through ONE
 * checkout-rebook intent FIRST; only once it commits does the checkout apply
 * (sale, completion, the "locked in" SMS, the rebook tally). On any failure
 * NOTHING is applied — no SMS, no tally, no half-rolled deposit — and the caller
 * branches on `kind` ('slot_taken' → back to the picker).
 *
 * Every other checkout (and the store-fallback build) is the synchronous
 * `checkOutBooking`, unchanged.
 */
export async function completeCheckout(input: CheckOutInput): Promise<CheckOutOutcome> {
  // The store-fallback build has no server: the synchronous checkout, unchanged.
  if (isStoreFallbackEnabled()) return { ok: true, result: checkOutBooking(input) };
  // L5 (tv-54xv): send every queued write first (a late check-in / seat PUT), so
  // nothing stale lands after the checkout commits and the server reads the
  // booking as it stands.
  await flushWritesAndWait();
  const cap = input.rebookCapture;
  const brain = useBrainStore.getState();
  const booking = brain.bookings[input.bookingId];
  if (!booking) return { ok: true, result: { bookingId: input.bookingId } };

  const at = nowIso();
  let serverRemainder = false;
  let serverRebook: ServerRebook | undefined;

  // tv-2na.8.17: a part-applied deposit — ONE apply-deposit intent carries the
  // rest where the booking goes (and, on a rebook, creates the next session in
  // the same transaction). Nothing applies unless it commits.
  const remainder = checkoutRemainder(input, booking, booking.depositId ? brain.deposits[booking.depositId] : undefined);
  if (remainder) {
    const rebookTo = remainder.toBookingId;
    const nextBooking =
      rebookTo && cap?.selectedSlotIso
        ? buildNextSession(booking, rebookTo, cap.selectedSlotIso, remainder.remainderDepositId, at, cap.preferBroughtForward)
        : undefined;
    const res = await applyDepositIntent({
      sourceBookingId: booking.id,
      appliedAud: remainder.appliedAud,
      remainderCase: remainder.remainderCase,
      remainderDepositId: remainder.remainderDepositId,
      ...(nextBooking ? { rebook: { bookingId: nextBooking.id, booking: nextBooking } } : {}),
    });
    if (!res.ok) return { ok: false, kind: res.kind, message: res.message };
    serverRemainder = true;
    if (nextBooking) serverRebook = { nextBooking };
  } else if (cap?.outcome.kind === 'rebookedInChair' && cap.selectedSlotIso) {
    const newBookingId = cap.outcome.newBookingId;
    const linked =
      input.rolloverDepositToNextBooking && booking.depositId ? brain.deposits[booking.depositId] : undefined;
    // Nothing free on it (all redeemed already): there's nothing to roll.
    const original = linked && availableToApplyAud(linked) > 0 ? linked : undefined;
    // Deterministic from the next session id: a retry after a lost response
    // replays the same ids (an idempotent no-op server-side).
    const roll = original ? buildRollover(booking, original, `dep-roll-${newBookingId}`, at, newBookingId) : undefined;
    const nextBooking = buildNextSession(booking, newBookingId, cap.selectedSlotIso, roll?.next.id, at, cap.preferBroughtForward);
    const res = await checkoutRebookIntent({
      sourceBookingId: booking.id,
      bookingId: newBookingId,
      booking: nextBooking,
      ...(roll ? { rolledDepositId: roll.next.id, rolledDeposit: roll.next } : {}),
    });
    if (!res.ok) return { ok: false, kind: res.kind, message: res.message };
    serverRebook = { nextBooking, ...(roll ? { roll } : {}) };
  }

  // The make-sale itself (L5, tv-54xv): the booking completed, the sale and its
  // till cash, in ONE server transaction. A repeat answers alreadyApplied.
  const sale = buildCheckoutSale(input, booking, at);
  // 14ym9bvbgg3 (c): the redemption, the rebook read and the healing check are
  // the server's too, so it needs to know whether the piece finished and the read.
  const pieceCompleted =
    Boolean(booking.pieceId && brain.pieces[booking.pieceId]) &&
    (input.markPieceComplete === true || cap?.outcome.kind === 'pieceComplete');
  const res = await floorCheckoutIntent({
    bookingId: booking.id,
    checkout: {
      amountPaidAud: input.amountPaidAud,
      paymentMethod: input.paymentMethod,
      ...(input.cameDown ? { cameDown: input.cameDown } : {}),
      ...(input.checkedOutBy ? { checkedOutBy: input.checkedOutBy } : {}),
      ...(input.sessionNote ? { sessionNotes: input.sessionNote } : {}),
    },
    sale,
    ...(pieceCompleted ? { pieceCompleted: true } : {}),
    ...(cap ? { rebook: { outcome: cap.outcome, attribution: cap.attribution } } : {}),
  });
  if (!res.ok) return { ok: false, kind: res.kind, message: res.message };
  if (res.alreadyApplied) {
    // Already checked out (a retry, a second tab, a re-run): mirror the server's
    // till and change nothing else — no second sale, SMS, tally or feed line.
    if (res.cashLedger) mirrorServerLedger(res.cashLedger);
    absorbCachedViews();
    return { ok: true, result: { bookingId: booking.id } };
  }
  return {
    ok: true,
    result: checkOutBooking(input, {
      ...(serverRemainder ? { serverRemainder: true } : {}),
      ...(serverRebook ? { serverRebook } : {}),
      serverCheckout: {
        sale: res.sale ?? sale,
        ...(res.cashLedger ? { cashLedger: res.cashLedger } : {}),
        echo: res,
      },
      at,
    }),
  };
}

/** The make-sale record (E1) for this checkout: the money record E3 reconciles and E2's cash ledger consumes. */
function buildCheckoutSale(input: CheckOutInput, booking: Booking, at: string): SalePayment {
  const brain = useBrainStore.getState();
  // Merch is a separate no-commission line.
  const merchLines = input.merchLines ?? [];
  const merchAud = merchLines.reduce((sum, l) => sum + l.priceAud, 0);
  const adjustmentAud = input.adjustmentAud ?? 0;
  const collectNowAud = input.amountPaidAud + merchAud + adjustmentAud;
  // Net cash to the till = tendered − change = the collect-now total when paid in
  // cash. Card settles to the bank (no till cash); mixed uses its cash portion.
  const cashInAud =
    input.paymentMethod === 'cash'
      ? collectNowAud
      : input.paymentMethod === 'mixed'
        ? Math.min(input.cashTenderedAud ?? 0, collectNowAud)
        : 0;
  const cust = brain.customers[booking.customerId];
  const artistRec = brain.artists[booking.artistId];
  return {
    id: `sale-${booking.id}`,
    bookingId: booking.id,
    studioId: booking.studioId,
    artistId: booking.artistId,
    customerName: cust ? `${cust.firstName} ${cust.lastName}` : booking.customerId,
    artistName: artistRec?.displayName ?? booking.artistId,
    method: input.paymentMethod,
    balanceAud: input.amountPaidAud,
    depositAppliedAud: input.depositAppliedAud ?? 0,
    merchAud,
    merchLines,
    adjustmentAud,
    adjustmentReason: input.adjustmentReason,
    cashInAud,
    saleReference: makeSaleReference(booking.studioId, booking.id, at),
    takenAt: at,
    afterCutoff: hourOfIso(at) >= SETTLEMENT_CUTOFF_HOUR,
  };
}

/** Fold the server's figures for a day ledger into the store, without a PUT back. */
function mirrorServerLedger(fig: ServerLedgerFigures): void {
  const brain = useBrainStore.getState();
  const local = brain.cashLedgers[fig.id] ?? cashLedgerFor(fig.studioId, fig.dayIso);
  withServerAuthoredWrites([['cashLedgers', fig.id]], () =>
    brain.upsertCashLedger({
      ...local,
      id: fig.id,
      openingFloatAud: fig.openingFloatAud,
      cashInAud: fig.cashInAud,
      payoutsAud: fig.payoutsAud,
      floatDipped: fig.floatDipped,
      ...(fig.countedAud != null ? { countedAud: fig.countedAud } : {}),
    }),
  );
}

export function checkOutBooking(
  input: CheckOutInput,
  opts: {
    serverRebook?: ServerRebook;
    serverRemainder?: boolean;
    /** L5: the floor-checkout intent already committed the sale and the till cash. */
    serverCheckout?: { sale: SalePayment; cashLedger?: ServerLedgerFigures; echo?: ServerCheckoutEcho };
    /** The instant the server-backed checkout stamped (keeps the sale and the booking in step). */
    at?: string;
  } = {},
): CheckOutResult {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[input.bookingId];
  if (!booking) return { bookingId: input.bookingId };
  const at = opts.at ?? nowIso();

  let rolledDepositId: string | undefined;
  const serverRebook = opts.serverRebook;
  // tv-2na.8.17: a part-applied deposit's remainder follows the booking. The
  // apply-deposit intent already committed it on the server-backed build: mirror
  // WITHOUT bridge PUTs (a PUT would replace the ledger the server appended to).
  const originalDeposit = booking.depositId ? brain.deposits[booking.depositId] : undefined;
  const remainder = checkoutRemainder(input, booking, originalDeposit);
  if (remainder && originalDeposit) {
    const plan = planRemainder(remainder, booking, originalDeposit, at);
    const recs = remainderRecords(plan, booking, originalDeposit, at);
    const write = () => {
      brain.upsertDeposit(recs.original);
      if (recs.remainder) brain.upsertDeposit(recs.remainder);
    };
    if (opts.serverRemainder) {
      withServerAuthoredWrites(
        [['deposits', recs.original.id], ...(recs.remainder ? [['deposits', recs.remainder.id] as const] : [])],
        write,
      );
    } else {
      write();
    }
    if (remainder.remainderCase === 'rolls-to-rebook' && recs.remainder) rolledDepositId = recs.remainder.id;
  } else if (serverRebook?.roll) {
    // The checkout-rebook intent already committed the roll (tv-2na.8.5): mirror
    // it into the store WITHOUT the bridge PUTs (a second PUT would replace the
    // ledger the server appended to).
    const { next, originalRolled } = serverRebook.roll;
    withServerAuthoredWrites(
      [
        ['deposits', next.id],
        ['deposits', originalRolled.id],
      ],
      () => {
        brain.upsertDeposit(next);
        brain.upsertDeposit(originalRolled);
      },
    );
    rolledDepositId = next.id;
  } else if (input.rolloverDepositToNextBooking && booking.depositId) {
    const original = brain.deposits[booking.depositId];
    if (original && availableToApplyAud(original) > 0) {
      const nextId = seedId('dep-roll') ?? `dep-roll-${booking.id}-${Date.now()}`;
      // tv-2na.11.6: an in-chair rebook rolls the money onto the new session.
      const cap = input.rebookCapture;
      const toBookingId =
        cap?.outcome.kind === 'rebookedInChair' && cap.selectedSlotIso ? cap.outcome.newBookingId : undefined;
      const { next, originalRolled } = buildRollover(booking, original, nextId, at, toBookingId);
      brain.upsertDeposit(next);
      brain.upsertDeposit(originalRolled);
      rolledDepositId = nextId;
    }
  }

  // On the server-backed build the floor-checkout intent already completed the
  // booking (and stamped the rebook read): mirror it, no PUT (14ym9bvbgg3 c). The
  // one exception is a local roll ("will rebook later" without a server roll),
  // whose new deposit id the server never saw: that booking still persists.
  const echo = opts.serverCheckout?.echo;
  const completed: Booking = {
    ...booking,
    status: 'completed',
    checkedOutAt: at,
    cameDown: input.cameDown,
    checkedOutBy: input.checkedOutBy,
    sessionNotes: input.sessionNote,
    amountPaidAud: echo?.booking?.amountPaidAud ?? input.amountPaidAud,
    paymentMethod: input.paymentMethod,
    paidAt: at,
    rolledDepositId,
    ...(echo?.booking?.depositId ? { depositId: echo.booking.depositId } : {}),
    ...(input.rebookCapture
      ? { rebookOutcome: input.rebookCapture.outcome, rebookAttribution: input.rebookCapture.attribution }
      : {}),
  };
  const localRoll = Boolean(rolledDepositId) && !serverRebook?.roll && !(remainder && opts.serverRemainder);
  if (opts.serverCheckout && !localRoll) {
    withServerAuthoredWrites([['bookings', booking.id]], () => brain.upsertBooking(completed));
  } else {
    brain.upsertBooking(completed);
  }
  // ontheday-7 (a): the pay-over-time plans the checkout closed stop chasing here too.
  for (const planId of echo?.topupPlansClosed ?? []) {
    const plan = useBrainStore.getState().topupPlans[planId];
    if (!plan) continue;
    withServerAuthoredWrites([['topupPlans', planId]], () =>
      brain.upsertTopupPlan({
        ...plan,
        status: 'completed',
        instalments: plan.instalments.map((i) =>
          i.status === 'scheduled' || i.status === 'reminded' || i.status === 'missed' ? { ...i, status: 'cancelled' } : i,
        ),
      }),
    );
  }
  // The deposits the intent settled (a pay-at-studio hold paid here) or redeemed:
  // their status and the ledger lines it appended, mirrored without a PUT.
  for (const d of echo?.deposits ?? []) {
    const local = useBrainStore.getState().deposits[d.id];
    if (!local) continue;
    const { ledgerAppended, id: _id, heldBookingId: _held, ...fields } = d;
    withServerAuthoredWrites([['deposits', d.id]], () =>
      brain.upsertDeposit({
        ...local,
        ...fields,
        ...(d.heldBookingId === null ? { heldBookingId: undefined } : {}),
        ledger: [...(local.ledger ?? []), ...ledgerAppended],
      }),
    );
  }

  // Came-down gate (Diagram 10): a client the artist did NOT walk down is a
  // tracked accountability issue, logged for Ops/owner follow-up.
  if (input.cameDown === 'did-not-come-down') {
    brain.appendActivity({
      id: `act-came-down-issue-${booking.id}-${at}`,
      type: 'floor-issue',
      customerId: booking.customerId,
      studioId: booking.studioId,
      // The artist's NAME, never the raw id (D10-14). The tracked issue itself is
      // the booking's cameDown (Ops worklist, until resolved), not this line.
      body: `Came-down gate: ${brain.artists[booking.artistId]?.displayName ?? 'the artist'} did NOT walk the client down at checkout.`,
      timestamp: at,
    });
  }

  // Record the make-sale (E1) and post its cash into the day's ledger (the
  // opening float is untouched by a sale). On the server-backed build the
  // floor-checkout intent already committed both: mirror them, no PUT (L5).
  if (opts.serverCheckout) {
    const { sale, cashLedger } = opts.serverCheckout;
    withServerAuthoredWrites([['salePayments', sale.id]], () => brain.upsertSalePayment(sale));
    if (cashLedger) mirrorServerLedger(cashLedger);
    echoTerminalDocket(sale); // W7 n1: the simulated terminal echoes the card portion at the sale
  } else {
    const sale = buildCheckoutSale(input, booking, at);
    brain.upsertSalePayment(sale);
    echoTerminalDocket(sale);
    if (sale.cashInAud > 0) {
      const ledger = cashLedgerFor(booking.studioId, at);
      brain.upsertCashLedger({ ...ledger, cashInAud: ledger.cashInAud + sale.cashInAud });
    }
  }

  const pieceId: string | undefined = booking.pieceId;
  // A final-session `pieceComplete` outcome (tv-2na.4) is itself the record that
  // the piece is done, so it completes the piece even without the checkbox flag.
  const markPieceComplete =
    input.markPieceComplete === true || input.rebookCapture?.outcome.kind === 'pieceComplete';
  let pieceCompleted = false;
  if (booking.pieceId) {
    const piece = brain.pieces[booking.pieceId];
    if (piece) {
      const added = input.hoursToAdd ?? booking.durationMin / 60;
      const sessionsCompleted = (piece.sessionsCompleted ?? piece.bookingsLinked.length) + 1;
      // L3: a partway decline updates the living estimate to completed-so-far +
      // remaining. Never overwrite sessionsCompleted, and never below it.
      const estimatedTotalSessions =
        !markPieceComplete && input.pieceSessionsLeft != null
          ? sessionsCompleted + Math.max(0, input.pieceSessionsLeft)
          : piece.estimatedTotalSessions;
      const capture = input.rebookCapture;
      const rebookedSession = serverRebook?.nextBooking ??
        (capture?.outcome.kind === 'rebookedInChair' && capture.selectedSlotIso
          ? buildNextSession(booking, capture.outcome.newBookingId, capture.selectedSlotIso, rolledDepositId, at, capture.preferBroughtForward)
          : undefined);
      // The first sitting is no longer a future appointment after checkout.
      // Keep an independently booked next sitting, or the one just rebooked.
      const nextSession = markPieceComplete ? undefined : [
        ...Object.values(brain.bookings), ...(rebookedSession ? [rebookedSession] : []),
      ].filter(b => b.id !== booking.id && b.pieceId === piece.id &&
        isLiveBooking(b) && b.status !== 'completed' && new Date(b.start).getTime() > new Date(at).getTime())
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())[0];
      const updated: Piece = {
        ...piece,
        hoursCompleted: Math.min(piece.estimatedTotalHours, piece.hoursCompleted + added),
        sessionsCompleted,
        estimatedTotalSessions,
        status: markPieceComplete ? 'completed' : 'in-progress',
        lastSessionDate: at,
        nextPlannedSession: nextSession?.start,
        // The intent linked the next session server-side: carry the link so this
        // piece PUT never drops it.
        ...(serverRebook
          ? {
              bookingsLinked: piece.bookingsLinked.includes(serverRebook.nextBooking.id)
                ? piece.bookingsLinked
                : [...piece.bookingsLinked, serverRebook.nextBooking.id],
            }
          : {}),
      };
      brain.upsertPiece(updated);
      pieceCompleted = markPieceComplete;
      // B8 (76vb, Anas 23 Sep): completing the piece resolves ITS open no-rebook
      // flags — the leak they tracked is closed.
      if (markPieceComplete) {
        for (const flag of Object.values(useBrainStore.getState().noRebookFlags)) {
          if (flag.pieceId === piece.id && !flag.resolvedAt) resolveNoRebookFlag(flag.id, { by: 'Piece complete' });
        }
      }
    }
  }

  // tv-b5g / Diagram 02: a deposit is "used up when the piece completes", and
  // tv-2na.11.7: a checkout that applies the deposit to today's balance (e.g. no
  // rebook, deposit applied) uses it too. Either way write ONE explicit
  // `redeemed` movement from the booking the deposit sat on to this booking, so
  // redemption is a record, not only a reporting derivation. Appended only; a
  // rolled deposit has already moved out, so skip it.
  const depositAppliedAud = input.depositAppliedAud ?? 0;
  if (
    !opts.serverCheckout && // the intent wrote it (14ym9bvbgg3 c)
    (pieceCompleted || depositAppliedAud > 0) &&
    booking.depositId &&
    !rolledDepositId &&
    !remainder
  ) {
    const redeemedDeposit = brain.deposits[booking.depositId];
    const ledger = redeemedDeposit?.ledger ?? [];
    const alreadyRedeemed = ledger.some((e) => e.kind === 'redeemed' && e.toBookingId === booking.id);
    if (redeemedDeposit && redeemedDeposit.status !== 'rolled-over' && !alreadyRedeemed) {
      const paid = depositPaidAud(redeemedDeposit);
      const redeemedAmount = pieceCompleted ? paid : Math.min(paid, depositAppliedAud);
      if (pieceCompleted || redeemedAmount > 0) {
        brain.upsertDeposit({
          ...redeemedDeposit,
          ledger: [
            ...ledger,
            {
              at,
              amountAud: redeemedAmount,
              kind: 'redeemed' as const,
              by: 'system',
              fromBookingId: redeemedDeposit.bookingId ?? booking.id,
              toBookingId: booking.id,
              ...(pieceId ? { toPieceId: pieceId } : {}),
              note: pieceCompleted
                ? 'Deposit redeemed on piece completion'
                : 'Deposit applied to the session balance',
            },
          ],
        });
      }
    }
  }

  if (input.sessionNote) {
    brain.addRapportNote(
      booking.customerId,
      input.sessionNote,
      `In-chair · ${booking.artistId}`,
      at,
    );
  }

  if (input.futureReadinessPhrase) {
    const signalId =
      seedId('frs-checkout') ??
      `frs-checkout-${booking.id}-${Math.floor(Math.random() * 1e6).toString(36)}`;
    const signal: FutureReadinessSignal = {
      id: signalId,
      customerId: booking.customerId,
      rawPhrase: input.futureReadinessPhrase,
      scheduledFor:
        input.futureReadinessTriggerDate ??
        new Date(new Date(at).getTime() + 28 * 24 * 60 * 60 * 1000).toISOString(),
      recommendedChannel: 'sms',
      status: 'scheduled',
      createdAt: at,
    };
    brain.addFutureReadinessSignal(signal);
  }

  brain.appendActivity({
    id: `act-checkout-${booking.id}`,
    type: 'booking-completed',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    amount: input.amountPaidAud,
    body: `${booking.customerId} checked out · paid $${input.amountPaidAud}${rolledDepositId ? ` · deposit rolled forward` : ''}.`,
    timestamp: at,
  });

  // Process the in-chair rebook outcome (if any). The brain store keeps the
  // outcome + attribution on the source booking; for Outcome A we also create
  // the next Booking record so the leaderboard / calendar pick it up; for
  // Outcome C we seed the sales follow-up task that lands in the queue.
  let newBookingId: string | undefined;
  if (input.rebookCapture) {
    const { outcome, attribution, selectedSlotIso, salesNote } = input.rebookCapture;
    // Already on the completed booking above (and on the server, when it checked out).
    if (!opts.serverCheckout) brain.setRebookOutcome(booking.id, outcome, attribution);

    if (outcome.kind === 'rebookedInChair' && selectedSlotIso) {
      const newBooking =
        serverRebook?.nextBooking ??
        buildNextSession(booking, outcome.newBookingId, selectedSlotIso, rolledDepositId, at);
      if (serverRebook) {
        // Already committed by the checkout-rebook intent — mirror, no PUT.
        withServerAuthoredWrites([['bookings', newBooking.id]], () => brain.upsertBooking(newBooking));
      } else {
        brain.upsertBooking(newBooking);
      }
      newBookingId = newBooking.id;
      // Next session is born `confirmed` — auto-send its waiver.
      onBookingConfirmed(newBooking.id);
      brain.appendActivity({
        id: `act-rebook-attr-${booking.id}`,
        type: 'rebook-attributed',
        customerId: booking.customerId,
        studioId: booking.studioId,
        artistId: booking.artistId,
        body: `Rebooked in chair · attributed to ${attribution.facilitatedBy} (${attribution.facilitatedByStaffId}).`,
        timestamp: at,
      });
    } else if (outcome.kind === 'willRebookLater') {
      // L3 (17 Jul): "will rebook later" IS the deferral now. Deposit rolls
      // forward as a held pot (rollover block above), no booking created, AND we
      // seed the same sales follow-up task that "defer to sales" used to, carrying
      // the mandatory reason as its note.
      brain.upsertRebookFollowup({
        id: `rfu-${booking.id}`,
        customerId: booking.customerId,
        studioId: booking.studioId,
        sourceBookingId: booking.id,
        originalArtistId: booking.artistId,
        note: outcome.note,
        createdAt: at,
      });
      brain.appendActivity({
        id: `act-rebook-later-${booking.id}`,
        type: 'rebook-attributed',
        customerId: booking.customerId,
        studioId: booking.studioId,
        artistId: booking.artistId,
        body: `Will rebook later · deposit rolled forward to hold${outcome.note ? ` · "${outcome.note}"` : ''} · sales to follow up.`,
        timestamp: at,
      });
    } else if (outcome.kind === 'noRebookDepositApplied') {
      // B8 (76vb): only a no-rebook MID-PIECE is a decline. A one-off (or a piece
      // finished today) with no future booking is just the end of the work.
      const midPiece = Boolean(booking.pieceId) && !markPieceComplete;
      const why = outcome.reason ? ` · ${outcome.reason}${outcome.reasonNote ? ` (${outcome.reasonNote})` : ''}` : '';
      brain.appendActivity({
        id: `act-rebook-decl-${booking.id}`,
        type: midPiece ? 'rebook-declined' : 'booking-completed',
        customerId: booking.customerId,
        studioId: booking.studioId,
        artistId: booking.artistId,
        body: midPiece
          ? `Declined a rebook mid-piece · reason: ${outcome.reason}${outcome.reasonNote ? ` (${outcome.reasonNote})` : ''}.`
          : `No future booking${booking.pieceId ? ' (piece complete)' : ' (one-off)'}${why}.`,
        timestamp: at,
      });
      // No rebook AND still mid-piece (a rebook needs no reason - the deposit just
      // rolls - but not rebooking mid-piece is the leak): raise the Ops flag with
      // the mandatory reason (tv-3gl leg a). Leg b (the Sweeper open-business
      // landing) is emergent from leaving the piece in-progress.
      if (booking.pieceId && !markPieceComplete) {
        flagNoRebookMidPiece({
          bookingId: booking.id,
          reason: String(outcome.reason),
          reasonNote: outcome.reasonNote,
        });
      }
    } else if (outcome.kind === 'rebookDeferredToSales') {
      brain.upsertRebookFollowup({
        id: outcome.salesTaskId,
        customerId: booking.customerId,
        studioId: booking.studioId,
        sourceBookingId: booking.id,
        originalArtistId: booking.artistId,
        note: salesNote,
        createdAt: at,
      });
      brain.appendActivity({
        id: `act-rebook-def-${booking.id}`,
        type: 'rebook-deferred-to-sales',
        customerId: booking.customerId,
        studioId: booking.studioId,
        artistId: booking.artistId,
        body: `Deferred rebook to sales team${salesNote ? ` · "${salesNote}"` : ''}.`,
        timestamp: at,
      });
    }
  }

  // Healing-check (09 Jul): a completed session that did NOT rebook in the chair
  // earns a T+7d "how's it healing?" nudge. Minted once per booking (stable id);
  // money cohort when a paid deposit still sits on the piece.
  if (input.rebookCapture?.outcome.kind !== 'rebookedInChair') {
    const pieceDeposit = booking.pieceId
      ? Object.values(brain.deposits).find(
          (d) => d.pieceId === booking.pieceId && d.status === 'paid' && depositPaidAud(d) > 0,
        )
      : undefined;
    const task = {
      id: `heal-${booking.id}`,
      customerId: booking.customerId,
      studioId: booking.studioId,
      sourceBookingId: booking.id,
      pieceId: booking.pieceId,
      depositId: pieceDeposit?.id,
      createdAt: at,
      dueAt: new Date(new Date(at).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    };
    // The floor-checkout intent minted it (14ym9bvbgg3 c): mirror, no PUT.
    if (opts.serverCheckout) {
      if (!brain.healingCheckTasks[task.id]) {
        withServerAuthoredWrites([['healingCheckTasks', task.id]], () => brain.upsertHealingCheck(task));
      }
    } else {
      brain.upsertHealingCheck(task);
    }
  }

  brain.incStat('inChairRebookings', input.rolloverDepositToNextBooking ? 1 : 0);
  playSound('deposit-chime');
  eventBus.publish('booking:checked-out', {
    bookingId: booking.id,
    rolledDepositId,
  });
  return { bookingId: booking.id, pieceId, pieceCompleted, rolledDepositId, newBookingId };
}

/**
 * The artist's session note (75mz: the artist never finishes the session; the
 * Floor does). Zack: "the manager deals with
 * all the rebooks" — so the artist doesn't record an outcome or take payment;
 * they just leave a rapport note (what they learned in the chair) that lands on
 * the customer record for the next conversation.
 *
 * Deliberately narrow: it never touches status, payment, or the rebook. A blank
 * note is a no-op. The manager owns checkout + the rebook read at the make-sale.
 */
export function recordArtistSessionNote(
  bookingId: string,
  note: string,
): Booking | undefined {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking) return undefined;

  const trimmed = note.trim();
  if (!trimmed) return booking;

  const at = nowIso();
  brain.addRapportNote(booking.customerId, trimmed, `In-chair · ${booking.artistId}`, at);
  brain.appendActivity({
    // One line per note: the artist can add more than one to a session.
    id: `act-artist-note-${booking.id}-${Date.parse(at)}`,
    type: 'booking-session-noted',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `${booking.artistId} left a session note on ${booking.customerId}'s record.`,
    timestamp: at,
  });
  eventBus.publish('booking:artist-session-note', { bookingId });
  return booking;
}

/** A photo the artist captured in the chair, tagged with what it is. */
export interface CapturedPhoto {
  url: string;
  kind: SessionPhotoKind;
}

/**
 * Photos the artist captured in the chair. They hang off the *piece* -
 * `Piece.progressPhotos` carries a `bookingId`, so each photo still knows which
 * sitting it came from and surfaces under that piece on the client's history.
 *
 * Each carries its `kind`: the work itself, or the reference it was worked from.
 * The caption names both the sitting and the kind, so a photo still explains
 * itself in a lightbox months later.
 *
 * Kept separate from `recordArtistSessionNote` on purpose: that function treats a
 * blank note as a no-op, so folding photos into it would silently drop them
 * whenever the artist adds pictures without writing anything.
 *
 * A booking with no piece has nothing to attach to - the caller disables capture
 * in that case, and this is a no-op as a backstop.
 */
export function recordArtistProgressPhotos(
  bookingId: string,
  photos: CapturedPhoto[],
  sessionNo?: number | null,
): Booking | undefined {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking) return undefined;
  if (photos.length === 0) return booking;
  if (!booking.pieceId) {
    // ontheday-2: a one-off session files its photos on the booking.
    void recordSessionPhotos(
      bookingId,
      photos.map((p) => ({ url: p.url, kind: p.kind, caption: sessionNo != null ? `Session ${sessionNo} ${p.kind}` : p.kind })),
    ).then((ok) => {
      if (!ok) {
        useUIStore.getState().pushNotification({
          intensity: 'high',
          title: "Photos didn't save",
          body: "The server didn't keep this session's photos. Try adding them again.",
          ttlMs: 8000,
        });
      }
    });
    return booking;
  }

  const at = nowIso();
  for (const photo of photos) {
    brain.addPieceProgressPhoto(booking.pieceId, {
      url: photo.url,
      capturedAt: at,
      bookingId,
      kind: photo.kind,
      caption: sessionNo != null ? `Session ${sessionNo} ${photo.kind}` : photo.kind,
    });
  }
  const refs = photos.filter((p) => p.kind === 'reference').length;
  brain.appendActivity({
    id: `act-artist-photos-${booking.id}`,
    type: 'booking-session-noted',
    customerId: booking.customerId,
    studioId: booking.studioId,
    artistId: booking.artistId,
    body: `${booking.artistId} added ${photos.length} photo${photos.length === 1 ? '' : 's'}${refs > 0 ? ` (${refs} reference)` : ''} to ${booking.customerId}'s piece.`,
    timestamp: at,
  });
  eventBus.publish('booking:artist-session-note', { bookingId });
  return booking;
}

/**
 * Maps a rebook read (will the customer come back) to the outcome the manager's
 * panel should open on. It's a starting point, not a decision: "will rebook"
 * opens on the in-chair rebook, "no rebook" opens on deposit-applied. No read →
 * no pre-selection. The manager is always free to override.
 */
export function prefillOutcomeFromIntent(
  intent: ArtistRebookIntent | undefined,
): RebookOutcome['kind'] | null {
  if (!intent) return null;
  return intent.willRebook ? 'rebookedInChair' : 'noRebookDepositApplied';
}

export function elapsedSeconds(checkedInIso: string, nowMs: number): number {
  return Math.max(0, Math.floor((nowMs - new Date(checkedInIso).getTime()) / 1000));
}

/** A photo to file for a session (the piece's when it has one, else the booking's). */
export interface SessionPhotoDraft {
  url: string;
  kind?: SessionPhotoKind;
  caption?: string;
  capturedAt?: string;
}

/**
 * ontheday-2 (L6a): photos of a one-off session used to be dropped, because a
 * photo could only file against a piece. A booking with no piece files them on
 * itself through the session-photos intent (idempotent per photo id); the store
 * mirrors what the server kept, with no PUT. A booking on a piece keeps the
 * piece path. Resolves false when the server refused or couldn't be reached.
 */
export async function recordSessionPhotos(bookingId: string, drafts: SessionPhotoDraft[]): Promise<boolean> {
  const brain = useBrainStore.getState();
  const booking = brain.bookings[bookingId];
  if (!booking || drafts.length === 0) return false;
  const at = nowIso();
  if (booking.pieceId) {
    for (const d of drafts) {
      brain.addPieceProgressPhoto(booking.pieceId, {
        url: d.url,
        capturedAt: d.capturedAt ?? at,
        bookingId,
        ...(d.kind ? { kind: d.kind } : {}),
        ...(d.caption ? { caption: d.caption } : {}),
      });
    }
    return true;
  }
  const have = new Set((booking.progressPhotos ?? []).map((p) => p.id));
  const photos = drafts.map((d, i) => ({
    id: `ph-${bookingId}-${Date.parse(d.capturedAt ?? at).toString(36)}-${i}-${Math.floor(Math.random() * 1e6).toString(36)}`,
    url: d.url,
    capturedAt: d.capturedAt ?? at,
    ...(d.caption ? { caption: d.caption } : {}),
    ...(d.kind ? { kind: d.kind } : {}),
  })).filter((p) => !have.has(p.id));
  if (photos.length === 0) return true;
  const mirror = () => {
    const now = useBrainStore.getState().bookings[bookingId];
    if (!now) return;
    withServerAuthoredWrites([['bookings', bookingId]], () =>
      useBrainStore.getState().upsertBooking({ ...now, progressPhotos: [...(now.progressPhotos ?? []), ...photos] }),
    );
  };
  if (isStoreFallbackEnabled()) {
    mirror();
    return true;
  }
  const res = await sessionPhotosIntent(bookingId, photos);
  if (!res.ok) return false;
  mirror();
  return true;
}
